import {
	type ChatStorePrivateState,
	type Conversation,
	isStoreAssistant,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { clearTracesIfNotEmpty } from './clearTracesIfNotEmpty';
import type { ChatStoreContainerProps } from './types';

/**
 * Applies a Guard RESPONSE_REDACTION to the assistant message in the chat store:
 *
 * - stamps `redactedResponse` so the redaction scribble UI renders,
 * - blanks the accumulated (unredacted) message content so the sensitive text is
 *   not left in the store,
 * - marks the turn `streamingComplete` so the thinking indicator does not linger,
 * - clears any live traces.
 *
 * The research-round content ("answer shown above the response") is cleared
 * separately via the research store (see clearResearchPlanContent / PRGD-488).
 *
 * Shared by the live send path (processStream) and the reconnect/replay path
 * (processReconnectStreamMessage) so both surfaces behave identically.
 */
export const applyResponseRedactionToMessage = ({
	conversationId,
	messageId,
	redactedResponse,
	getMessages,
	setMessages,
	getState,
	updateCurrentConversation,
}: {
	conversationId: string;
	messageId: string;
	redactedResponse: string;
	getMessages: ChatStoreContainerProps['getMessages'];
	setMessages: ChatStoreContainerProps['setMessages'];
	getState: () => ChatStorePrivateState;
	updateCurrentConversation: (data: Partial<Conversation>) => void;
}): void => {
	const chat = getMessages(conversationId) ?? [];

	// Prefer the assistant message that matches the redacted messageId; otherwise
	// fall back to the most recent assistant message (the current turn).
	let targetIdx = -1;
	for (let i = 0; i < chat.length; i++) {
		const message = chat[i];
		if (isStoreAssistant(message)) {
			if (message.content?.message?.message?.id === messageId) {
				targetIdx = i;
				break;
			}
			targetIdx = i;
		}
	}

	if (targetIdx >= 0) {
		const target = chat[targetIdx];
		if (isStoreAssistant(target)) {
			const existingInnerMessage = target.content?.message?.message;
			const newChat = [...chat];
			newChat[targetIdx] = {
				...target,
				redactedResponse,
				content: {
					...target.content,
					streamingComplete: true,
					message: {
						...target.content.message,
						message: {
							...(existingInnerMessage ?? {}),
							content: '',
						},
					},
				},
			};
			setMessages(conversationId, newChat);
		}
	}

	// Drop any live "thinking" traces that streamed before the redacted response.
	clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
};
